import { useState } from "react";
import type { FormEvent } from "react";
import { adminApi } from "../lib/api";
import { Button, TextInput } from "../control/ui/primitives";

/** The /admin password screen. Deliberately spare — no branding hints, no links. */
export function AdminLogin({ onSuccess }: { onSuccess: () => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError(null);
    try {
      await adminApi.login(password);
      onSuccess();
    } catch {
      setError("Incorrect password.");
      setPassword("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-ink)] p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-2xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-8 shadow-2xl"
      >
        <div className="mb-6">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            Restricted
          </div>
          <h1 className="mt-1 text-xl font-semibold text-zinc-100">Admin access</h1>
        </div>

        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-400">
            Password
          </span>
          <TextInput
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            autoComplete="current-password"
          />
        </label>

        <div
          className={`mt-2 min-h-[1.1rem] text-xs ${error ? "text-red-400" : "text-transparent"}`}
          aria-live="polite"
        >
          {error ?? "placeholder"}
        </div>

        <Button
          type="submit"
          variant="primary"
          className="mt-2 w-full"
          disabled={busy || !password}
        >
          {busy ? "Checking…" : "Enter"}
        </Button>
      </form>
    </div>
  );
}
